import React, { useState } from "react";
import KeyInsightMiniEditor from "./KeyInsightMiniEditor";

interface KeyInsightsEditorProps {
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
}

const getPlainText = (html: string) =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const getInsightPreview = (insight: string, index: number) => {
  const plainText = getPlainText(insight);

  if (!plainText) {
    return `Key takeaway ${index + 1}`;
  }

  return plainText.length > 68 ? `${plainText.slice(0, 68)}...` : plainText;
};

const KeyInsightsEditor = ({ value, onChange, error }: KeyInsightsEditorProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(
    value.length ? 0 : null,
  );

  const handleAddInsight = () => {
    const nextInsights = [...value, ""];
    onChange(nextInsights);
    setExpandedIndex(nextInsights.length - 1);
  };

  const handleRemoveInsight = (index: number) => {
    const nextInsights = value.filter((_, insightIndex) => insightIndex !== index);
    onChange(nextInsights);

    if (!nextInsights.length) {
      setExpandedIndex(null);
      return;
    }

    if (expandedIndex === index) {
      setExpandedIndex(Math.min(index, nextInsights.length - 1));
    } else if (expandedIndex !== null && expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  const handleMoveInsight = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;

    if (nextIndex < 0 || nextIndex >= value.length) {
      return;
    }

    const nextInsights = [...value];
    const [movingInsight] = nextInsights.splice(index, 1);
    nextInsights.splice(nextIndex, 0, movingInsight);
    onChange(nextInsights);
    setExpandedIndex(nextIndex);
  };

  const handleInsightChange = (index: number, insight: string) => {
    const nextInsights = [...value];
    nextInsights[index] = insight;
    onChange(nextInsights);
  };

  return (
    <section className={`key-insights-section ${error ? "error" : ""}`}>
      <div className="key-insights-header">
        <div>
          <h3>Key Takeaways *</h3>
          <p>Add the main takeaways readers should remember from this post.</p>
        </div>
        <button
          className="key-insights-add-button"
          onClick={handleAddInsight}
          type="button"
        >
          Add key takeaway
        </button>
      </div>

      {error && <span className="field-error">{error}</span>}

      {!value.length ? (
        <div className="key-insights-empty">
          <p>No key takeaways added yet.</p>
          <button onClick={handleAddInsight} type="button">
            Add your first key takeaway
          </button>
        </div>
      ) : (
        <div className="key-insights-list">
          {value.map((insight, index) => {
            const isExpanded = expandedIndex === index;

            return (
              <div
                className={`key-insight-accordion ${
                  isExpanded ? "expanded" : ""
                }`}
                key={index}
              >
                <button
                  className="key-insight-accordion-header"
                  onClick={() => setExpandedIndex(isExpanded ? null : index)}
                  type="button"
                >
                  <span className="key-insight-rank">{index + 1}</span>
                  <span className="key-insight-preview">
                    {getInsightPreview(insight, index)}
                  </span>
                  <span className="key-insight-toggle" />
                </button>
                <div className="key-insight-actions">
                  <button
                    aria-label="Move key takeaway up"
                    data-tooltip="Move up"
                    disabled={index === 0}
                    onClick={() => handleMoveInsight(index, -1)}
                    type="button"
                  >
                    ↑
                  </button>
                  <button
                    aria-label="Move key takeaway down"
                    data-tooltip="Move down"
                    disabled={index === value.length - 1}
                    onClick={() => handleMoveInsight(index, 1)}
                    type="button"
                  >
                    ↓
                  </button>
                  <button
                    aria-label="Remove key takeaway"
                    data-tooltip="Remove"
                    onClick={() => handleRemoveInsight(index)}
                    type="button"
                  >
                    ×
                  </button>
                </div>
                {isExpanded && (
                  <div className="key-insight-accordion-body">
                    <KeyInsightMiniEditor
                      value={insight}
                      onChange={(nextInsight) =>
                        handleInsightChange(index, nextInsight)
                      }
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default KeyInsightsEditor;
